import type { GeneratedProjectActorsQuery as ProjectActorsQuery } from '../../common/graphql/types';
import type { RoleId, ActorType } from '../../common/types';
import type {
	ActorOperationActionData,
	ActorsOperation,
	ActorWithoutRoleDescription,
	GeneratedProjectActor,
	RenderProps,
} from './types';

const performOperationOnActors = (
	data: ProjectActorsQuery,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	operation: ActorOperationActionData | Record<any, any>,
	actorsOperation: ActorsOperation,
): ProjectActorsQuery => {
	if (data && data.projectActors && operation.id) {
		const { projectActors } = data;
		const { actors } = projectActors;
		if (Array.isArray(actors) && actors.length > 0) {
			const remainingActors = actorsOperation(actors, operation.id, operation.roles);
			const newData = {
				projectActors: {
					...projectActors,
					actors: remainingActors,
				},
			};
			return newData;
		}
	}
	return data;
};

export const removeActorFromData = (
	data: ProjectActorsQuery,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	operation: ActorOperationActionData | Record<any, any>,
): ProjectActorsQuery =>
	performOperationOnActors(data, operation, (actors, id) =>
		actors.filter((actor) => actor && actor.roleTypeId !== id),
	);

export const updateActorFromData = (
	data: ProjectActorsQuery,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	operation: ActorOperationActionData | Record<any, any>,
): ProjectActorsQuery =>
	performOperationOnActors(data, operation, (actors, id, roles) =>
		actors.map((actor) => {
			if (actor && actor.roleTypeId === id) {
				return { ...actor, roles: roles || [] };
			}
			return actor;
		}),
	);

export const getRenderPropsDataWithoutActors = (data: RenderProps): RenderProps => ({
	...data,
	data: {
		...(data.data || {}),
		projectActors: {
			...(data.data
				? data.data.projectActors
				: {
						isLastBatch: false,
						__typename: 'ProjectActorResponse',
					}),
			// @ts-expect-error TS2322 - Type 'null' is not assignable to type 'readonly ProjectActorsQuery_projectActors_actors[]'.
			actors: null,
		},
	},
});

export const getIsLastBatch = (data?: ProjectActorsQuery): boolean =>
	data && data.projectActors ? !!data.projectActors.isLastBatch : false;

export const getActorsFromQuery = (data?: ProjectActorsQuery): ActorWithoutRoleDescription[] => {
	if (
		data &&
		data.projectActors &&
		data.projectActors.actors &&
		Array.isArray(data.projectActors.actors)
	) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return data.projectActors.actors.reduce<Array<any>>(
			(acc: ActorWithoutRoleDescription[], actor?: GeneratedProjectActor | null) => {
				if (!actor) {
					return acc;
				}
				const { accountId, active, avatarUrl, displayName, email, roleTypeId, roles, type } = actor;
				if (!(roleTypeId && type && roles && Array.isArray(roles) && displayName)) {
					return acc;
				}

				const actorWithoutRoleDescription: ActorWithoutRoleDescription = {
					__typename: 'ProjectActor',
					accountId: accountId || undefined,
					active: !!active,
					avatarUrl: avatarUrl || undefined,
					displayName,
					email: email || undefined,
					roleTypeId,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					roleIds: roles as ReadonlyArray<RoleId>,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: type as ActorType,
				};
				acc.push(actorWithoutRoleDescription);
				return acc;
			},
			[],
		);
	}
	return [];
};
